import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, Rocket, Sparkles, Loader2, ShieldAlert, CheckCircle2, XCircle, MousePointerClick,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCreditGate } from "@/components/nadia/CreditGate";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { Button } from "@/components/ui/button";
import {
  analyzeRetargeting, buildRetargetingAudience, buildRetargetingCampaign,
  getRetargetingCampaign, approveRetargeting, getTrackingStatus,
} from "@/lib/api";

export default function Retargeting() {
  const [analysis, setAnalysis] = useState(null);
  const [current, setCurrent] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    analyzeRetargeting().then(setAnalysis).catch(() => setAnalysis(null));
    getRetargetingCampaign().then(setCurrent).catch(() => setCurrent(null));
    getTrackingStatus().then(setTracking).catch(() => setTracking(null));
  };
  useEffect(() => { load(); }, []);

  const audience = current?.audience;
  const draft = current?.draft;

  const buildAudience = async () => {
    setBusy("audience");
    try { await buildRetargetingAudience(); load(); toast.success("Pubblico creato"); }
    catch { toast.error("Errore"); }
    finally { setBusy(null); }
  };
  const { gate, dialog: creditDialog } = useCreditGate();
  const buildDraft = () => gate("deep_marketing_analysis", async (op_id) => {
    setBusy("draft");
    try { await buildRetargetingCampaign(op_id); load(); toast.success("Bozza creativa pronta"); }
    catch (e) { if (e?.response?.status !== 402) toast.error("Errore"); throw e; }
    finally { setBusy(null); }
  });
  const approve = async () => {
    setBusy("approve");
    try {
      const res = await approveRetargeting();
      if (res.approved) toast.success("Campagna approvata");
      else toast.error(res.message);
      load();
    } catch { toast.error("Errore"); }
    finally { setBusy(null); }
  };

  const trackingReady = tracking && tracking.pixel.connected && tracking.capi.connected && tracking.consent_mode.connected;

  return (
    <>
      {creditDialog}
      <PageHeader
        eyebrow="Retargeting"
        title="Ricontatta chi non ha convertito"
        subtitle="Nadia trasforma i visitatori della landing in un pubblico dedicato e propone un annuncio caldo. Nulla parte senza il tuo ok."
      />

      {/* Analysis */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatCard label="Visitatori totali" value={analysis?.total_visitors ?? "—"} icon={Users} />
        <StatCard label="Convertiti" value={analysis?.converted ?? "—"} icon={CheckCircle2} />
        <StatCard label="Da ricontattare" value={analysis?.non_converted ?? "—"} icon={MousePointerClick} accent />
      </div>

      {/* Nadia note */}
      <div className="nadia-card p-5 mb-6 flex items-start gap-4" data-testid="retargeting-nadia">
        <NadiaAvatar size="sm" />
        <div>
          <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wider mb-1">Nadia dice</div>
          <p className="text-sm text-slate-200 leading-relaxed">
            {audience
              ? `Ho identificato ${audience.size} persone che hanno visto la landing senza convertire. Preparo un annuncio dedicato — decidi tu quando pubblicarlo.`
              : "Analizzo chi ha visitato la landing negli ultimi 14 giorni ma non ha ancora convertito. Da lì costruisco un pubblico e una creatività calda."}
          </p>
        </div>
      </div>

      {/* Requirements */}
      <div className="nadia-card p-5 mb-6" data-testid="tracking-requirements">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={14} className="text-amber-300" />
          <h3 className="font-heading text-sm font-semibold text-slate-100">Requisiti per pubblicare</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ReqCard label="Meta Pixel" ok={tracking?.pixel.connected} envs={tracking?.pixel.env_required} />
          <ReqCard label="Conversions API" ok={tracking?.capi.connected} envs={tracking?.capi.env_required} />
          <ReqCard label="Consent Mode" ok={tracking?.consent_mode.connected} envs={tracking?.consent_mode.env_required} hint="Cookiebot, Iubenda o CMP nativa" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          onClick={buildAudience}
          disabled={busy === "audience" || !analysis?.non_converted}
          variant="outline"
          className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent"
          data-testid="build-audience-button"
        >
          {busy === "audience" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Users size={14} className="mr-2" />}
          {audience ? "Aggiorna pubblico" : "Crea pubblico"}
        </Button>
        <Button
          onClick={buildDraft}
          disabled={busy === "draft" || !audience}
          variant="outline"
          className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent"
          data-testid="build-draft-button"
        >
          {busy === "draft" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
          {draft ? "Rigenera creatività" : "Genera creatività"}
        </Button>
        <Button
          onClick={approve}
          disabled={busy === "approve" || !draft || draft.status === "approved"}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
          data-testid="approve-retargeting-button"
        >
          {busy === "approve" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Rocket size={14} className="mr-2" />}
          {draft?.status === "approved" ? "Approvata" : "Approva e pubblica"}
        </Button>
      </div>

      {/* Draft preview */}
      {draft && (
        <div className="nadia-card p-5" data-testid="retargeting-draft-card">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-violet-500/10 text-violet-300 border border-violet-500/25">
              {draft.status === "approved" ? "APPROVATA" : "In attesa di approvazione"}
            </span>
            <span className="text-xs text-slate-500">
              Budget €{draft.budget_daily}/giorno · {draft.duration_days} giorni
            </span>
          </div>
          <h3 className="font-heading text-2xl font-bold text-slate-50 mb-2">{draft.headline}</h3>
          <p className="text-sm text-slate-300 leading-relaxed mb-3">{draft.primary_text}</p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA</span>
            <span className="inline-flex px-2 py-1 rounded-md text-xs font-bold bg-white/[0.03] border border-white/10 text-slate-100">
              {draft.cta}
            </span>
          </div>
          {!trackingReady && (
            <div className="mt-4 p-3 rounded-xl bg-amber-500/[0.06] border border-amber-500/25 text-xs text-amber-200">
              Non pubblicherò senza Pixel + CAPI + Consent Mode configurati. Aggiungi le variabili in <b>Impostazioni → Tracking</b>.
            </div>
          )}
        </div>
      )}
    </>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`nadia-card p-4 ${accent ? "border-cyan-500/20" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
        <Icon size={14} className="text-slate-500" />
      </div>
      <div className="font-heading text-2xl font-bold text-slate-50 mt-1">{value}</div>
    </div>
  );
}

function ReqCard({ label, ok, envs, hint }) {
  return (
    <div className={`p-4 rounded-xl border ${ok ? "border-emerald-500/25 bg-emerald-500/[0.04]" : "border-amber-500/25 bg-amber-500/[0.04]"}`}>
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-amber-400" />}
        <span className="text-sm font-semibold text-slate-100">{label}</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-1 font-mono truncate">
        {ok ? "Collegato" : "Configurazione necessaria"}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
